import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Check, ChevronsUpDown, Lock } from 'lucide-react';

/**
 * Two-step role picker: the client/admin FIRST selects a Package Tier (one of the 8
 * organizational tiers), THEN the Role dropdown shows ONLY that tier's roles (searchable by
 * name + alias, scoped to the tier). Changing the tier clears a role that doesn't belong to it.
 *
 * context='client_booking' => roles that are not yet bookable (pending pricing) are shown but
 * DISABLED (cannot be selected for a live booking). Other contexts (talent, directory) allow
 * selecting not-yet-bookable roles. Billing is unaffected — the selected role object (incl
 * billing_fold) is returned to the caller so the 3-tier billing fold stays accurate.
 */
export default function TierRoleCombobox({ value, onChange, context = 'client_booking', testid = 'tier-role-combobox', showLabels = true }) {
  const [open, setOpen] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [tierKey, setTierKey] = useState('');
  const [selected, setSelected] = useState(null);

  const requireBookable = context === 'client_booking';

  useEffect(() => {
    let active = true;
    api.get('/roles/by-tier', { params: { context } })
      .then(({ data }) => { if (active) setTiers((data.tiers || []).filter((t) => (t.roles || []).length > 0)); })
      .catch(() => {});
    return () => { active = false; };
  }, [context]);

  // Sync external value -> internal tier + selected role (edit mode / parent reset).
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    for (const t of tiers) {
      const r = (t.roles || []).find((x) => x.id === value);
      if (r) { setSelected({ ...r, tier_label: t.label }); setTierKey(t.key); return; }
    }
  }, [value, tiers]);

  const activeTier = useMemo(() => tiers.find((t) => t.key === tierKey) || null, [tiers, tierKey]);
  const tierRoles = activeTier ? (activeTier.roles || []) : [];

  const onTierChange = (key) => {
    setTierKey(key);
    // Clear a previously-selected role if it doesn't belong to the newly chosen tier.
    const next = tiers.find((t) => t.key === key);
    if (selected && !(next?.roles || []).some((r) => r.id === selected.id)) {
      setSelected(null);
      onChange?.(null, null);
    }
  };

  const pick = (role, tierLabel) => {
    if (requireBookable && !role.bookable) return; // guard: not selectable for live booking
    setSelected({ ...role, tier_label: tierLabel });
    onChange?.(role.id, role);
    setOpen(false);
  };

  return (
    <div className="space-y-3" data-testid={`${testid}-wrap`}>
      {/* Step 1 — Package Tier */}
      <div className="space-y-1.5">
        {showLabels && <Label className="text-gray-700">Package Tier *</Label>}
        <Select value={tierKey} onValueChange={onTierChange}>
          <SelectTrigger className="w-full bg-white" data-testid={`${testid}-tier`}>
            <SelectValue placeholder="Select a package tier…" />
          </SelectTrigger>
          <SelectContent>
            {tiers.map((t) => (
              <SelectItem key={t.key} value={t.key} data-testid={`${testid}-tier-opt-${t.key}`}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Step 2 — Role (filtered to the selected tier only) */}
      <div className="space-y-1.5">
        {showLabels && <Label className="text-gray-700">Role *</Label>}
        <Popover open={open} onOpenChange={(o) => tierKey && setOpen(o)}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={!tierKey}
              className="w-full justify-between font-normal"
              data-testid={testid}
            >
              <span className={`truncate ${selected ? '' : 'text-muted-foreground'}`}>
                {selected ? selected.name : (tierKey ? 'Select role…' : 'Select a package tier first')}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search role or alias…" data-testid={`${testid}-search`} />
              <CommandList className="max-h-72">
                <CommandEmpty>No matching role in this tier.</CommandEmpty>
                <CommandGroup heading={activeTier?.label}>
                  {tierRoles.map((r) => {
                    const disabled = requireBookable && !r.bookable;
                    return (
                      <CommandItem
                        key={r.id}
                        value={`${r.name} ${(r.aliases || []).join(' ')}`}
                        disabled={disabled}
                        onSelect={() => pick(r, activeTier?.label)}
                        data-testid={`${testid}-opt-${r.id}`}
                        className={disabled ? 'opacity-50' : ''}
                      >
                        <Check className={`mr-2 h-4 w-4 ${value === r.id ? 'opacity-100' : 'opacity-0'}`} />
                        <span className="flex-1 truncate">{r.name}</span>
                        {!r.bookable && (
                          <Badge variant="outline" className="ml-2 text-[10px] text-amber-600">
                            <Lock className="h-3 w-3 mr-0.5" />{requireBookable ? 'pending pricing' : 'coming soon'}
                          </Badge>
                        )}
                        {(r.qualifications || []).length > 0 && (
                          <Badge variant="outline" className="ml-1 text-[10px] text-indigo-500">cert</Badge>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
